import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { STAGES, CHECKLIST_TASKS } from "./StageChecklist";

// One chip per operational stage, in lifecycle order. Grouping stages used to
// hide real work from the operator: a shipment sitting in Documentation read
// "Airline Booking", and one being invoiced read "In Delivery", so the rail
// could never show that the money side was still open.
const STAGE_TO_LABEL_MAP = {
  Query: [STAGES.QUERY_STAGE],
  Quote: [STAGES.QUOTE_STAGE],
  Nomination: [STAGES.NOMINATION_STAGE],
  Collection: [STAGES.COLLECTION_STAGE],
  "Airline Booking": [STAGES.AIRLINE_BOOKING_STAGE],
  Documentation: [STAGES.DOCUMENTATION_STAGE],
  "Handed over": [STAGES.HANDOVER_STAGE],
  "In Delivery": [STAGES.POST_DELIVERY_STAGE],
  Invoicing: [STAGES.INVOICING_BILLING_STAGE],
  Completed: [STAGES.COMPLETED],
};

const stepLabels = Object.keys(STAGE_TO_LABEL_MAP);

// Get the first uncompleted task label for a given stage
const getNextTaskLabel = (stage) => {
  const tasks = CHECKLIST_TASKS[stage];
  if (!tasks || tasks.length === 0) return null;
  return tasks[0]?.label || null;
};

export default function HeaderProgressBar({
  currentStage,
  onStageClick,
  variant = "default",
}) {
  const isIntegrated = variant === "integrated";
  const isBoard = variant === "board";
  let currentIndex = -1;
  // Completed now owns the last chip, so it is found by the normal lookup — but
  // the whole rail still has to read done, not "you are working on Completed".
  const isFinished = currentStage === STAGES.COMPLETED;

  if (currentStage === STAGES.CANCELLED) {
    currentIndex = -2;
  } else {
    currentIndex = stepLabels.findIndex((label) =>
      STAGE_TO_LABEL_MAP[label].includes(currentStage)
    );
  }

  const steps = stepLabels.map((label, index) => {
    let status = "upcoming";
    if (currentIndex === -2) {
      status = "cancelled";
    } else if (isFinished || index < currentIndex) {
      status = "completed";
    } else if (index === currentIndex) {
      status = "current";
    }
    return { label, status };
  });

  const progressWidth =
    isFinished
      ? `100%`
      : currentIndex >= 0
        ? `${((currentIndex + 0.5) / steps.length) * 100}%`
        : `0%`;

  const nextTask = currentStage ? getNextTaskLabel(currentStage) : null;
  const currentStepNumber = currentIndex >= 0 ? currentIndex + 1 : 0;
  const totalSteps = stepLabels.length;
  const currentStepLabel =
    currentIndex >= 0 && currentIndex < stepLabels.length
      ? stepLabels[currentIndex]
      : currentStage === STAGES.COMPLETED
        ? "Completed"
        : currentStage === STAGES.CANCELLED
          ? "Cancelled"
          : "Awaiting stage";
  const statusText =
    currentStage === STAGES.CANCELLED
      ? "Shipment stopped"
      : currentStage === STAGES.COMPLETED
        ? "Shipment complete"
        : currentIndex >= 0
          ? `Step ${currentStepNumber} of ${totalSteps}`
          : "No checklist stage yet";

  const wrapperClasses = isIntegrated
    ? "border-t border-slate-200 pt-4"
    : isBoard
      ? "min-w-0 w-full max-w-full rounded-[20px] border border-slate-200 bg-slate-50 px-3 py-3 lg:flex lg:flex-col"
    : "overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)] dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.225_0.026_264)]";

  const headerClasses = isIntegrated
    ? "flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
    : "border-b border-slate-200 px-5 py-5 dark:border-[oklch(0.3_0.02_264/0.6)]";

  const headerContentClasses = isIntegrated
    ? ""
    : "flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between";

  const eyebrowClasses = isIntegrated
    ? "text-[11px] uppercase tracking-[0.22em] text-slate-400"
    : "text-[11px] uppercase tracking-[0.24em] text-slate-400 dark:text-slate-500";

  const titleClasses = isIntegrated
    ? "text-lg font-semibold text-slate-900"
    : "text-xl font-semibold text-slate-900 dark:text-white";

  const nextTaskCardClasses = isIntegrated
    ? "rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3"
    : "rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.6)]";

  const nextTaskLabelClasses = isIntegrated
    ? "text-[11px] uppercase tracking-[0.18em] text-slate-400"
    : "text-[11px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500";

  const nextTaskValueClasses = isIntegrated
    ? "mt-1 flex items-center gap-2 text-sm font-medium text-slate-900"
    : "mt-1 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200";

  const progressTrackClasses = isIntegrated
    ? "relative mb-3 h-1.5 overflow-hidden rounded-full bg-slate-200"
    : isBoard
      ? "relative mb-3 h-1.5 overflow-hidden rounded-full bg-slate-200"
    : "relative mb-4 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-[oklch(0.26_0.028_264)]";

  const progressFillClasses =
    currentIndex === -2
      ? "bg-red-500"
      : isBoard
        ? "bg-gradient-to-r from-emerald-500 to-fk-accent shadow-[0_0_10px_-1px_rgba(37,99,235,0.6)]"
        : "bg-fk-accent";

  return (
    <div className={wrapperClasses}>
      {!isBoard && (
        <div className={headerClasses}>
          <div className={headerContentClasses}>
            <div>
              <p className={eyebrowClasses}>
                Shipment stage rail
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h2 className={titleClasses}>
                  {currentStepLabel}
                </h2>
                <span
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                    currentStage === STAGES.CANCELLED
                      ? isIntegrated
                        ? "bg-red-50 text-red-600"
                        : "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                      : currentStage === STAGES.COMPLETED
                        ? isIntegrated
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                        : isIntegrated
                          ? "bg-blue-50 text-fk-accent"
                          : "bg-blue-50 text-fk-accent dark:bg-blue-500/10 dark:text-blue-300"
                  }`}
                >
                  {statusText}
                </span>
              </div>
            </div>

            {nextTask && currentIndex >= 0 && currentIndex !== -2 && (
              <div className={nextTaskCardClasses}>
                <p className={nextTaskLabelClasses}>
                  Next task
                </p>
                <div className={nextTaskValueClasses}>
                  <span>{nextTask}</span>
                  {onStageClick && (
                    <button
                      onClick={() => onStageClick("current-task")}
                      className={`inline-flex items-center gap-1 text-xs font-semibold ${
                        isIntegrated ? "text-fk-accent" : "text-fk-accent dark:text-blue-300"
                      }`}
                    >
                      Open <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isBoard && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              Stages
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700">
              {currentStepLabel}
            </span>
            <span className="text-xs text-slate-500">
              {statusText}
            </span>
          </div>
          {nextTask && currentIndex >= 0 && currentIndex !== -2 && (
            <span className="text-xs text-slate-500">
              Next: <span className="font-medium text-slate-700">{nextTask}</span>
            </span>
          )}
        </div>
      )}

      <div className={`${isIntegrated ? "mt-3" : isBoard ? "min-w-0 flex-1 lg:min-h-0 lg:flex lg:flex-col" : "px-4 py-4"}`}>
        <div className={`${progressTrackClasses} ${isBoard ? "w-full max-w-full shrink-0" : ""}`}>
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${progressFillClasses}`}
            style={{ width: progressWidth }}
          />
        </div>

        <div
          className={
            isBoard
              ? "min-w-0 w-full overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:thin] lg:flex-1 lg:min-h-0 lg:pr-0.5"
              : "overflow-x-auto"
          }
        >
          <div
            className={`flex pb-1 ${isBoard ? "flex-nowrap gap-1.5 sm:gap-2 lg:w-full lg:gap-2.5" : "min-w-max gap-2"}`}
          >
            {steps.map((step, i) => {
              const isInteractive = !!onStageClick;
              const boardStatusLabel =
                step.status === "completed"
                  ? "Done"
                  : step.status === "current"
                    ? "Active"
                    : step.status === "cancelled"
                      ? "Stopped"
                      : "Soon";
              const cardClasses =
                isBoard
                  ? step.status === "completed"
                    ? "border-slate-200/70 bg-white text-slate-800 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_6px_14px_-8px_rgba(15,23,42,0.14)]"
                    : step.status === "current"
                      ? "border-blue-200 bg-blue-50 text-fk-accent shadow-[0_3px_14px_-4px_rgba(37,99,235,0.26)]"
                      : step.status === "cancelled"
                        ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
                        : "border-slate-200/70 bg-white text-slate-400 shadow-[0_1px_3px_rgba(15,23,42,0.05)]"
                : isIntegrated
                  ? step.status === "completed"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : step.status === "current"
                      ? "border-blue-200 bg-blue-50 text-fk-accent shadow-[0_10px_24px_rgba(37,99,235,0.10)]"
                      : step.status === "cancelled"
                        ? "border-red-200 bg-red-50 text-red-600"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                  : step.status === "completed"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                    : step.status === "current"
                      ? "border-blue-200 bg-blue-50 text-fk-accent shadow-[0_12px_30px_rgba(37,99,235,0.12)] dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300"
                      : step.status === "cancelled"
                        ? "border-red-200 bg-red-50 text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                        : "border-slate-200 bg-white text-slate-500 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400";

              return (
                <button
                  key={step.label}
                  type="button"
                  title={step.label}
                  className={`group relative shrink-0 border text-left transition ${
                    isBoard
                      ? "min-w-[5.75rem] max-w-[6.75rem] rounded-xl px-2 py-1.5 sm:min-w-[6.5rem] sm:max-w-none sm:rounded-[14px] sm:px-2.5 sm:py-2 lg:flex lg:min-h-[88px] lg:min-w-0 lg:flex-1 lg:basis-0 lg:flex-col lg:justify-between xl:rounded-[16px] xl:px-3 xl:py-3"
                      : "min-w-[148px] rounded-[18px] px-3.5 py-2.5"
                  } ${
                    isInteractive ? "hover:-translate-y-0.5" : "cursor-default"
                  } ${cardClasses}`}
                  onClick={() => isInteractive && onStageClick(step.label)}
                >
                  <div className={`flex items-start justify-between gap-1.5 ${isBoard ? "" : "gap-3"}`}>
                    <span
                      className={`min-w-0 font-semibold uppercase leading-tight tracking-wide ${
                        isBoard
                          ? "line-clamp-2 text-[9px] tracking-[0.12em] sm:text-[10px] xl:text-xs xl:tracking-[0.18em]"
                          : "text-xs tracking-[0.18em]"
                      }`}
                    >
                      {step.label}
                    </span>
                    <div
                      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${
                        isBoard
                          ? "h-6 w-6 text-[10px] sm:h-7 sm:w-7 xl:h-8 xl:w-8 xl:text-[11px]"
                          : "h-8 w-8 text-[11px]"
                      } ${
                        isBoard
                          ? step.status === "completed"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-600"
                            : step.status === "current"
                              ? "border border-fk-accent bg-fk-accent text-white"
                              : step.status === "cancelled"
                                ? "border border-red-200 bg-red-50 text-red-600"
                                : "border border-slate-300 bg-white text-slate-400"
                          : `border border-current/15 ${isIntegrated ? "bg-white" : "bg-white/70 dark:bg-[oklch(0.168_0.021_264/0.4)]"}`
                      }`}
                    >
                      {step.status === "completed" ? (
                        <FontAwesomeIcon icon={faCheck} className={isBoard ? "text-[9px] xl:text-[11px]" : "text-[11px]"} />
                      ) : step.status === "cancelled" ? (
                        <FontAwesomeIcon icon={faTimes} className={isBoard ? "text-[9px] xl:text-[11px]" : "text-[11px]"} />
                      ) : (
                        i + 1
                      )}
                    </div>
                  </div>
                  <p
                    className={`font-semibold ${
                      isBoard
                        ? `mt-1 text-[9px] leading-tight sm:text-[10px] xl:mt-2 xl:text-[10px] ${
                            step.status === "completed"
                              ? "text-emerald-600"
                              : step.status === "current"
                                ? "text-fk-accent"
                                : step.status === "cancelled"
                                  ? "text-red-500"
                                  : "text-slate-400"
                          }`
                        : "mt-3 text-[11px] leading-5 opacity-80"
                    }`}
                  >
                    {isBoard
                      ? boardStatusLabel
                      : step.status === "completed"
                        ? "Completed"
                        : step.status === "current"
                          ? "Active working stage"
                          : step.status === "cancelled"
                            ? "Stopped"
                            : "Upcoming"}
                  </p>
                  {isBoard && step.status === "current" && (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "3px",
                        borderRadius: "0 0 16px 16px",
                        background: "linear-gradient(90deg, var(--blue, #2563eb), var(--blue-2, #3b82f6))",
                        animation: "fkqw-glow 2s ease-in-out infinite",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
